import { PieceType } from '../types.ts';

/**
 * Converts a Chess960 position ID (0–959) to a back-rank array of 8 pieces.
 * Uses the standard Fischer Random Chess encoding:
 *   id %  4          → light-square bishop column (b/d/f/h = 1/3/5/7)
 *   (id / 4)  %  4   → dark-square bishop  column (a/c/e/g = 0/2/4/6)
 *   (id / 16) %  6   → queen in the 6 remaining squares (left-to-right index)
 *   (id / 96)        → knight pair from the 10 possible arrangements of 2 knights in 5 squares
 *   rooks + king fill the last 3 squares in order: R K R
 */
export const chess960PositionFromId = (id: number): PieceType[] => {
  // Clamp to valid range
  const spId = Math.max(0, Math.min(959, Math.round(id)));

  const rank: (PieceType | null)[] = Array(8).fill(null);

  // 1. Light-square bishop (columns 1, 3, 5, 7)
  const lightBishopIndex = spId % 4;
  const lightBishopCol = lightBishopIndex * 2 + 1; // 1, 3, 5, 7
  rank[lightBishopCol] = 'B';

  // 2. Dark-square bishop (columns 0, 2, 4, 6)
  const darkBishopIndex = Math.floor(spId / 4) % 4;
  const darkBishopCol = darkBishopIndex * 2; // 0, 2, 4, 6
  rank[darkBishopCol] = 'B';

  // 3. Queen in remaining 6 empty squares
  const queenIndex = Math.floor(spId / 16) % 6;
  const emptyAfterBishops = rank
    .map((p, i) => (p === null ? i : null))
    .filter((i): i is number => i !== null);
  rank[emptyAfterBishops[queenIndex]] = 'Q';

  // 4. Knights — 10 arrangements of 2 knights in the 5 remaining squares
  //    Table: knight pair positions within the 5 empty squares (0-indexed)
  const knightTable: [number, number][] = [
    [0, 1], [0, 2], [0, 3], [0, 4],
    [1, 2], [1, 3], [1, 4],
    [2, 3], [2, 4],
    [3, 4],
  ];
  const knightPairIndex = Math.floor(spId / 96); // 0–9
  const emptyAfterQueen = rank
    .map((p, i) => (p === null ? i : null))
    .filter((i): i is number => i !== null);
  const [n1, n2] = knightTable[knightPairIndex];
  rank[emptyAfterQueen[n1]] = 'N';
  rank[emptyAfterQueen[n2]] = 'N';

  // 5. Remaining 3 squares → R K R (queenside rook, king, kingside rook)
  const lastThree = rank
    .map((p, i) => (p === null ? i : null))
    .filter((i): i is number => i !== null);
  rank[lastThree[0]] = 'R';
  rank[lastThree[1]] = 'K';
  rank[lastThree[2]] = 'R';

  return rank as PieceType[];
};

/** Short back-rank string, e.g. "RNBQKBNR" */
export const chess960PositionLabel = (id: number): string => {
  return chess960PositionFromId(id).join('');
};

/** Human-readable label for a position, e.g. "#518 – RNBQKBNR (Standard)" */
export const chess960PositionDisplay = (id: number): string => {
  const label = chess960PositionLabel(id);
  const suffix = id === 518 ? ' (Standard)' : '';
  return `#${id} — ${label}${suffix}`;
};

/** Build the full list of 960 positions with id + label for the selector */
export interface Chess960PositionOption {
  id: number;
  label: string; // "RNBQKBNR"
  display: string; // "#518 — RNBQKBNR (Standard)"
}

export const ALL_CHESS960_POSITIONS: Chess960PositionOption[] = Array.from(
  { length: 960 },
  (_, i) => ({
    id: i,
    label: chess960PositionLabel(i),
    display: chess960PositionDisplay(i),
  })
);

const normalizeSearchText = (value: string): string => {
  return value
    .trim()
    .toUpperCase()
    .replace(/KNIGHTS?/g, 'N')
    .replace(/BISHOPS?/g, 'B')
    .replace(/ROOKS?/g, 'R')
    .replace(/QUEENS?/g, 'Q')
    .replace(/KINGS?/g, 'K')
    .replace(/[^A-Z0-9]/g, '');
};

const isPiecePatternSearch = (query: string): boolean => /^[BKNRQ]+$/.test(query);

const getSubsequenceScore = (target: string, query: string): number => {
  let queryIndex = 0;
  let firstMatchIndex = -1;
  let lastMatchIndex = -1;

  for (let index = 0; index < target.length && queryIndex < query.length; index += 1) {
    if (target[index] === query[queryIndex]) {
      if (firstMatchIndex === -1) {
        firstMatchIndex = index;
      }
      lastMatchIndex = index;
      queryIndex += 1;
    }
  }

  if (queryIndex !== query.length || firstMatchIndex === -1 || lastMatchIndex === -1) {
    return -1;
  }

  const span = lastMatchIndex - firstMatchIndex;
  return 250 - firstMatchIndex * 10 - span;
};

const getPatternScore = (target: string, query: string): number => {
  if (!query) {
    return -1;
  }

  if (target === query) {
    return 700;
  }

  if (target.startsWith(query)) {
    return 600 - (target.length - query.length);
  }

  const containsIndex = target.indexOf(query);
  if (containsIndex >= 0) {
    return 450 - containsIndex * 10;
  }

  return getSubsequenceScore(target, query);
};

export const searchChess960Positions = (query: string, limit = 40): Chess960PositionOption[] => {
  const rawQuery = query.trim();
  if (!rawQuery) {
    return ALL_CHESS960_POSITIONS.slice(0, limit);
  }

  const numericQuery = rawQuery.replace(/^#/, '');
  const normalizedQuery = normalizeSearchText(rawQuery);
  const isNumericSearch = /^\d+$/.test(numericQuery);
  const isPieceSearch = normalizedQuery.length > 0 && !isNumericSearch && isPiecePatternSearch(normalizedQuery);

  if (isPieceSearch) {
    const prefixMatches = ALL_CHESS960_POSITIONS.filter((position) =>
      position.label.startsWith(normalizedQuery)
    );

    if (prefixMatches.length > 0) {
      return prefixMatches.slice(0, limit);
    }
  }

  return ALL_CHESS960_POSITIONS
    .map((position) => {
      let score = -1;

      if (isNumericSearch) {
        if (position.id === Number(numericQuery)) {
          score = 1000;
        } else if (String(position.id).startsWith(numericQuery)) {
          score = 900 - String(position.id).length;
        }
      }

      if (normalizedQuery) {
        const labelScore = getPatternScore(position.label, normalizedQuery);
        score = Math.max(score, labelScore);
      }

      return { position, score };
    })
    .filter((entry) => entry.score >= 0)
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }
      return left.position.id - right.position.id;
    })
    .slice(0, limit)
    .map((entry) => entry.position);
};

/**
 * Format Chess960 position for display in UI components.
 * Returns "Random position" for null, or formatted position string for valid IDs.
 */
export const formatChess960Position = (positionId: number | null): string => {
  if (positionId === null) {
    return 'Random position';
  }
  return chess960PositionDisplay(positionId);
};

/** The major pieces that can occupy the queen slot. */
export const MAJOR_PIECE_TYPES: PieceType[] = ['Q', 'H', 'E', 'A'];

const isMajorPieceSlot = (piece: PieceType): boolean => MAJOR_PIECE_TYPES.includes(piece);

/**
 * Back rank for a Chess960 position id, with the queen slot replaced by the
 * chosen major piece (used by Blind Chess960). E.g. id 518 → "RNBQKBNR".
 */
export const chess960BackRankFromId = (id: number, majorPiece: PieceType = 'Q'): string => {
  return chess960PositionFromId(id)
    .map((piece) => (piece === 'Q' ? majorPiece : piece))
    .join('');
};

/** A uniformly random valid Chess960 back rank with the given major piece. */
export const randomChess960BackRank = (majorPiece: PieceType = 'Q'): string => {
  const id = Math.floor(Math.random() * 960);
  return chess960BackRankFromId(id, majorPiece);
};

/**
 * Inverse of chess960PositionFromId: map a back rank back to its Chess960
 * position id (0–959). The queen slot may contain any major piece
 * (Q/H/E/A). Returns null when the rank is not a valid Chess960 arrangement.
 */
export const chess960PositionIdFromRank = (rank: string | PieceType[]): number | null => {
  const raw = typeof rank === 'string' ? rank.split('') : rank;
  if (raw.length !== 8) return null;
  const pieces = raw as PieceType[];

  const lightBishopCol = pieces.findIndex((piece, col) => piece === 'B' && col % 2 === 1);
  const darkBishopCol = pieces.findIndex((piece, col) => piece === 'B' && col % 2 === 0);
  if (lightBishopCol === -1 || darkBishopCol === -1) return null;

  const lightIndex = (lightBishopCol - 1) / 2;
  const darkIndex = darkBishopCol / 2;

  const emptyAfterBishops = pieces
    .map((piece, col) => (piece === 'B' ? null : col))
    .filter((col): col is number => col !== null);
  const queenIndex = emptyAfterBishops.findIndex((col) => isMajorPieceSlot(pieces[col]));
  if (queenIndex === -1) return null;

  const emptyAfterQueen = pieces
    .map((piece, col) => (piece === 'B' || isMajorPieceSlot(piece) ? null : col))
    .filter((col): col is number => col !== null);
  const knightSlots: number[] = [];
  emptyAfterQueen.forEach((col, index) => {
    if (pieces[col] === 'N') knightSlots.push(index);
  });
  if (knightSlots.length !== 2) return null;

  const knightTable: [number, number][] = [
    [0, 1], [0, 2], [0, 3], [0, 4],
    [1, 2], [1, 3], [1, 4],
    [2, 3], [2, 4],
    [3, 4],
  ];
  const [firstKnight, secondKnight] = knightSlots;
  const knightPairIndex = knightTable.findIndex(
    ([left, right]) => left === firstKnight && right === secondKnight
  );
  if (knightPairIndex === -1) return null;

  return lightIndex + 4 * darkIndex + 16 * queenIndex + 96 * knightPairIndex;
};
